/**
 * 사고 처리 — 목록(EA-24)·두 번째 문(EA-25)·문이 열린 뒤(EA-26)·처리(EA-27)·외부 인계(EA-28)·
 * 자료 메일 보내기(EA-76)·보낸 자료 메일 보기(EA-77).
 *
 * 계약: XM-56(목록, page — 검색 `q`·기간 `from`·`to`)·XM-58(열람)·XM-59(사진)·XM-60(인계)·XM-61(처리)·XM-114(자료 메일).
 *
 * 두 겹이다. **목록은 조회**지만 **상세는 조회가 아니다** — 두 번째 문 뒤에 있어서
 * 사유를 적고 비밀번호를 다시 넣어야 열리고, 여는 것 자체가 감사 기록에 남는다.
 * 그래서 상세는 useQuery 가 아니라 버튼으로만 부르고, 열린 내용은 화면 상태에만 둔다 —
 * 새로고침하면 다시 문이다(레거시 그대로).
 *
 * ⚠ **처리는 돈이 움직인다**(XM-61 — 거래완료 = 에스크로 Confirm, 거래취소 = 에스크로 Cancel, 이 둘만:
 * 거래 흐름 04 9단계, 사장님 원문 9 「관리자페이지에서 거래완료 또는 거래취소상태로 스마트컨트렉트 상태를
 * 변경할 수 있다」). 결과는 체인에서 확인한 뒤 서버가 사고를 닫는다. 이 화면은 요청을 보내고 목록을 다시 읽는다.
 *
 * 자료 메일(거래 흐름 04 8단계 — 사장님 원문 8 「거래소 관리자는 관련 거래 자료들을 확인하고 사용자에게
 * 관련 자료들을 메일로 보내준다」): 한 당사자를 골라 그 회원의 가입 이메일로 보낸다. 보낸 기록은 상세
 * (XM-58 `materialMails`)에 보이고, 보낸 답으로 화면의 기록에 한 줄을 더한다.
 *
 * ⚠ **PIN 에 막혀도 쓰기를 기억하지 않는다**(레거시 그대로 — 명세 1절 머리말의 예외).
 */
import { createContext, useContext, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import {
  actOnIncident, fetchIncidentDocument, fetchProofPhoto, handOverIncident, listIncidents, openIncident,
  previewIncidentMaterialMail, sendIncidentMaterialMail, uploadIncidentDocument,
} from '@/api/admin'
import { ApiError, errorMessage } from '@/api/errors'
import { useSession } from '@/auth/session'
import { clearBlocked, formatLeft, usePasswordCheckBlock } from '@/auth/passwordCheck'
import {
  AdminRef, Button, Card, Code, Field, Labeled, LINK, Maybe, MemberLink, Notice, NUM, Row, TD,
  TextArea, TH, Time,
} from '@/components/ui'
import { Confirm, submitWrite, useWrite, ViewDialog } from '@/components/Confirm'
import { EnglishOnlyNote, hasKorean } from '@/components/EnglishOnly'
import { QueryState } from '@/components/QueryState'
import { PagePager } from '@/components/Pager'
import { Choice, DateInput, FilterBar, Search, useFilters } from '@/components/Filters'
import { DateField } from '@/components/DateFields'
import { localDayRange, usde } from '@/format'
import {
  CANCEL_CAUSE_LABEL, CHAT_SENDER_LABEL, DEPOSIT_TYPE_LABEL, INCIDENT_ACTION_LABEL, INCIDENT_EVENT_ACTION_LABEL,
  INCIDENT_STATUS_LABEL, MAIL_LANGUAGE_LABEL, MATERIAL_MAIL_ROLE_LABEL, MATERIAL_MAIL_STATUS_LABEL, PROOF_RESULT_LABEL,
  PROOF_ROLE_LABEL, TRADE_SOURCE_LABEL, TRADE_STAGE_LABEL, VERDICT_LABEL,
  type IncidentAction, type IncidentCase, type IncidentDetail, type IncidentDocument, type IncidentEvent,
  type IncidentMaterialMail, type IncidentProof, type IncidentStatus, type IncidentVerdict,
  type MaterialMailPhoto, type MaterialMailRole, type Millis, type TradeChat,
} from '@/api/types'
import { MATERIAL_MAIL } from './MaterialMailTemplate'
import { Plain } from '@/components/Versioned'

const SIZE = 25
const FILTERS = ['status', 'q', 'from', 'to'] as const

/**
 * 상태 거르개 — 처음 값은 접수됨(제안 03 4번, 7절 C-08). 주소에 값이 없으면 접수됨,
 * `ALL` 이면 거르지 않는다.
 */
function statusParam(v: string | undefined): IncidentStatus | undefined {
  if (v === 'ALL') return undefined
  if (v === 'PROCESSING' || v === 'CLOSED') return v
  return 'OPEN'
}

/* ── EA-24 목록 (XM-56) ──────────────────────────────────────────── */

/**
 * 사고 목록. 2026-10-01 사장님 「1번으로 하는데 거래 시간 항목이 왜없지」(질문 25 「1」): 검색 칸 하나(주문번호·
 * 거래번호·아이디·지갑 주소 가운데 무엇이든 — 2026-10-02 사장님 「1」부터 서버가 일부만 넣어도 찾는다(두 글자부터, 대소문자
 * 가리지 않음 — 한 글자는 통째로 같은 것만), 기간 칸(거래 시각 = 매칭 시각의 날짜,
 * 관리자 컴퓨터 시간 — 끝 날도 넣는다), 칸 거래 시각·주문번호·판매자 아이디·구매자 아이디(거래소백엔드 da01075).
 * 거르개는 주소에 싣는다(기간은 `from`·`to` = yyyy-mm-dd).
 */
export function IncidentsPage() {
  const f = useFilters(FILTERS)
  const fromDay = f.values['from'] ?? ''
  const toDay = f.values['to'] ?? ''
  const q = (f.values['q'] ?? '').trim()
  const params = {
    status: statusParam(f.values['status']),
    q: q === '' ? undefined : q,
    // 기간의 끝 날도 넣는다 — 끝 날의 다음 날 00:00(to 미포함). 날로 읽히지 않으면 그쪽은 보내지 않는다.
    from: localDayRange(fromDay)?.from,
    to: localDayRange(toDay)?.to,
    page: f.page,
    size: SIZE,
  }
  const list = useQuery({ queryKey: ['incidents', params], queryFn: () => listIncidents(params) })
  const rows = list.data?.items ?? []

  return (
    <Card title="Incidents">
      <p className="mb-3 text-xs text-slate-500">
        Filter incidents by status, search and period. At first only received incidents are shown, newest first. For
        search, type an order number, trade number, ID or wallet address, or a part of one (2 characters or more).
        Upper and lower case are the same. The period is the date of the trade time (match time).
      </p>
      <FilterBar active={f.active} onClear={f.clear}>
        <Choice label="Status" value={f.values['status'] ?? ''} anyLabel="Received"
          options={[
            { value: 'PROCESSING', label: 'Processing' },
            { value: 'CLOSED', label: 'Closed' },
            { value: 'ALL', label: 'All' },
          ]}
          onChange={(v) => f.set('status', v)} />
        <Search label="Search" value={f.values['q'] ?? ''} placeholder="Order no., trade no., ID or wallet address"
          onChange={(v) => f.set('q', v)} />
        <DateInput label="Period start" value={fromDay} allowEmpty onChange={(v) => f.set('from', v)} />
        <DateInput label="Period end" value={toDay} allowEmpty onChange={(v) => f.set('to', v)} />
      </FilterBar>
      {/* 거르개를 하나라도 걸었으면(상태·검색·기간 — 「거르개 지우기」가 보이는 때) 걸러서 없는 것이다(시험 T2 2026-10-02). */}
      <QueryState isPending={list.isPending} error={list.error} empty={rows.length === 0}
        emptyText={f.active ? 'No incidents match the filters.' : 'No incidents have been received.'}>
        <table className="w-full text-sm">
          <thead><tr>
            <th className={TH}>No.</th><th className={TH}>Trade time</th><th className={TH}>Trade</th>
            <th className={TH}>Order no.</th><th className={TH}>Seller</th><th className={TH}>Buyer</th>
            <th className={TH}>Cause</th><th className={TH}>Status</th><th className={TH}>Pending action</th>
            <th className={TH}>Assignee</th><th className={TH}>Quantity</th><th className={TH}>Received at</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td className={TD}>
                  <Link className={LINK} to={`/incidents/${r.id}`}>{r.id}</Link>
                </td>
                <td className={TD}><Time ms={r.tradeAt} /></td>
                <td className={`${TD} font-mono text-xs`}>{r.orderId}</td>
                <td className={`${TD} font-mono text-xs`}><Maybe value={r.orderNo} /></td>
                <td className={TD}><MemberLink memberId={r.sellerMemberId} loginId={r.sellerLoginId} /></td>
                <td className={TD}><MemberLink memberId={r.buyerMemberId} loginId={r.buyerLoginId} /></td>
                <td className={TD}><Code>{r.cause}</Code></td>
                <td className={TD}><Labeled labels={INCIDENT_STATUS_LABEL} code={r.status} /></td>
                <td className={TD}>{r.pendingAction ? <Code>{r.pendingAction}</Code> : <Maybe value={null} />}</td>
                <td className={TD}><Maybe value={r.assigneeLoginId} /></td>
                <td className={NUM}><Maybe value={usde(r.amount)} /></td>
                <td className={TD}><Time ms={r.openedAt} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </QueryState>
      <PagePager page={f.page} size={SIZE} total={list.data?.total} onChange={f.setPage} />
    </Card>
  )
}

/* ── 상세 ────────────────────────────────────────────────────────── */

/**
 * 문이 열린 뒤 화면이 쓰는 모양 — 서버가 null 로 줄 수 있는 목록(거래 쪽을 못 읽었을 때 `proofs`·`verdicts`, 앞 판
 * 서버의 `documents`)을 빈 목록으로 읽는다.
 */
type Opened = Omit<IncidentDetail, 'proofs' | 'verdicts' | 'documents'> & {
  proofs: IncidentProof[]
  verdicts: IncidentVerdict[]
  documents: IncidentDocument[]
}

function opened(d: IncidentDetail): Opened {
  return {
    ...d,
    proofs: d.proofs ?? [],
    verdicts: d.verdicts ?? [],
    documents: d.documents ?? [],
    materialMails: d.materialMails ?? [],
  }
}

export function IncidentDetailPage() {
  const { id = '' } = useParams()
  const incidentId = Number(id)
  const [detail, setDetail] = useState<Opened | null>(null)

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Link to="/incidents" className="text-xs text-slate-500 underline">← Incident queue</Link>
      </div>
      {/* ⚠ 함수로 고친다 — 한 처리 안에서 문서(onDocument)와 사고(onIncident)가 차례로 오면 앞 것이 지워지지 않게. */}
      {detail
        ? <IncidentOpened incidentId={incidentId} detail={detail}
            onIncident={(incident) => setDetail((d) => d && { ...d, incident })}
            onMail={(mail) => setDetail((d) => d && { ...d, materialMails: [...d.materialMails, mail] })}
            onDocument={(doc) => setDetail((d) => d && { ...d, documents: [...d.documents, doc] })} />
        : <SecondDoor incidentId={incidentId} onOpened={(d) => setDetail(opened(d))} />}
    </div>
  )
}

/**
 * EA-25 — 사유 + 비밀번호를 받아 문을 연다(XM-58).
 *
 * ⚠ **틀린 비밀번호도 기록된다**(서버가 `INCIDENT_DETAIL_DENIED` 를 남긴다). 그 사실을
 * 화면이 미리 말한다 — 몰래 두드려 보는 문이 아니다.
 *
 * 정할 것 37(21번 표로 정해짐 — 3회 경고·5회 임시 잠금·10회 연속 최종 잠금): 틀린 비밀번호는 내 비밀번호 바꾸기(XM-08)와 한 셈 —
 * 서버가 횟수마다 다른 글을 준다(errors.ts). 5분 막힘(429 `password_check_blocked`)은 남은 시간을 보이고 끝날 때까지 문을 잠그며
 * (passwordCheck.ts), 최종 잠금(423 `account_locked` — 서버가 모든 세션을 끝냄)은 그 글을 들고 로그인 화면으로 나간다.
 */
function SecondDoor({ incidentId, onOpened }: {
  incidentId: number
  onOpened: (d: IncidentDetail) => void
}) {
  const { signOut } = useSession()
  const blocked = usePasswordCheckBlock()
  const [reason, setReason] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = () => {
    if (busy) return
    setBusy(true); setError(null)
    openIncident(incidentId, reason.trim(), password)
      .then((d) => { clearBlocked(); onOpened(d) })
      .catch((e: unknown) => {
        if (e instanceof ApiError && e.status === 404) setError('This incident does not exist.')
        else if (e instanceof ApiError && e.code === 'account_locked') signOut(errorMessage(e, 'incident_open'))
        else if (e instanceof ApiError && e.code === 'password_check_blocked') blocked.onBlocked(errorMessage(e, 'incident_open'))
        else setError(errorMessage(e, 'incident_open'))
      })
      .finally(() => setBusy(false))
  }

  return (
    <Card title={`Incident #${incidentId} — second door`}>
      <div className="flex max-w-md flex-col gap-3">
        <Notice tone="warn">
          Opening this door shows the members' bank receipt photos and reading values. It opens
          <strong> only after who opened it and why are written to the audit log</strong>.
        </Notice>
        <Field label="Reason for viewing" value={reason} maxLength={200}
          placeholder="Write why you open it — it is kept in the audit log as written"
          onChange={(e) => setReason(e.target.value)} />
        <Field label="Password (enter again)" type="password" value={password}
          onChange={(e) => setPassword(e.target.value)} />
        {blocked.seconds !== null ? (
          <Notice tone="stop">
            {blocked.message}
            <span className="mt-1 block font-mono tabular-nums">Time left: {formatLeft(blocked.seconds)}</span>
          </Notice>
        ) : error && <Notice tone="stop">{error}</Notice>}
        <div>
          <Button onClick={submit} disabled={busy || reason.trim() === '' || password === '' || blocked.seconds !== null}>
            {busy ? 'Opening…' : 'Open the door'}
          </Button>
        </div>
      </div>
    </Card>
  )
}

/* ── EA-26 문이 열린 뒤 ──────────────────────────────────────────── */

/**
 * 문이 열린 뒤. 2026-10-01 사장님 「관리자는 다 봐야지」(질문 27): 단계 기록(시각과 함께)·체인 거래 번호(예치·종료)·
 * 두 사람 지갑 주소·결제 마감·연장 횟수·취소 까닭, 약관 동의 판·시각(질문 12 「1」), 사진마다 판독 결과(질문 17),
 * 사고처리 중인 거래의 대화(질문 26 — 사고처리가 끝나면 지워짐), 올린 문서(질문 30)를 모두 보인다(거래소백엔드
 * f9a1767·96d06a6·999cd8f).
 */
function IncidentOpened({ incidentId, detail, onIncident, onMail, onDocument }: {
  incidentId: number
  detail: Opened
  onIncident: (i: IncidentCase) => void
  onMail: (m: IncidentMaterialMail) => void
  onDocument: (d: IncidentDocument) => void
}) {
  const i = detail.incident
  const t = detail.trade
  const photos = detail.proofs.filter((p) => p.hasPhoto)
  const [viewingDoc, setViewingDoc] = useState<number | null>(null)
  const photoCache = usePhotoCache(incidentId)

  return (
    <PhotoCacheCtx.Provider value={photoCache}>
      <Card title={`Incident #${incidentId} · Trade ${i.orderId}`}>
        <dl className="grid grid-cols-[9rem_1fr] gap-2 text-sm">
          <Row label="Status"><Labeled labels={INCIDENT_STATUS_LABEL} code={i.status} /></Row>
          <Row label="Cause"><Code>{i.cause}</Code></Row>
          <Row label="Assignee"><AdminRef adminId={i.assigneeAdminId} loginId={i.assigneeLoginId} /></Row>
          <Row label="Pending action">{i.pendingAction ? <Code>{i.pendingAction}</Code> : <Maybe value={null} />}</Row>
          <Row label="Close result">{i.closeResult ? <Code>{i.closeResult}</Code> : <Maybe value={null} />}</Row>
          <Row label="Order no.">
            <span className="font-mono"><Maybe value={i.orderNo ?? detail.timeline?.orderNo ?? null} /></span>
          </Row>
          {t && (
            <>
              <Row label="Trade quantity"><span className="font-mono">{usde(t.amount)}</span></Row>
              <Row label="Fee"><span className="font-mono"><Maybe value={usde(t.fee)} /></span></Row>
              <Row label="Deposit amount"><span className="font-mono"><Maybe value={usde(t.depositAmount)} /></span></Row>
              <Row label="Type"><Labeled labels={DEPOSIT_TYPE_LABEL} code={t.depositType} /></Row>
              <Row label="Source"><Labeled labels={TRADE_SOURCE_LABEL} code={t.source} /></Row>
              <Row label="Seller (sends coins)"><MemberLink memberId={t.sellerMemberId} loginId={i.sellerLoginId} /></Row>
              <Row label="Seller wallet address"><Address value={t.sellerAddress} /></Row>
              <Row label="Buyer (receives coins)"><MemberLink memberId={t.buyerMemberId} loginId={i.buyerLoginId} /></Row>
              <Row label="Buyer wallet address"><Address value={t.buyerAddress} /></Row>
              <Row label="Matched at"><Time ms={t.matchedAt} /></Row>
              <Row label="Payment deadline"><Time ms={t.paymentDeadline} /></Row>
              <Row label="Extensions used"><Maybe value={t.extendsUsed} unit=" times" /></Row>
              <Row label="Stage"><Labeled labels={TRADE_STAGE_LABEL} code={t.stage} /></Row>
              <Row label="Cancel cause"><Labeled labels={CANCEL_CAUSE_LABEL} code={t.cancelCause ?? null} /></Row>
              <Row label="Party's cancel reason">
                {t.cancellationReason
                  ? <><Code>{t.cancellationReason}</Code>{t.cancellationDetail && <> {t.cancellationDetail}</>}</>
                  : <Maybe value={null} />}
              </Row>
            </>
          )}
        </dl>
        {detail.tradeUnavailable && (
          <div className="mt-3">
            <Notice tone="warn">Could not read the trade side. <Code>{detail.tradeUnavailable}</Code></Notice>
          </div>
        )}
      </Card>

      <Card title="Stage history">
        {!detail.timeline
          ? <p className="text-sm text-slate-500">No stage history.</p>
          : (
            <>
              <dl className="mb-3 grid grid-cols-[9rem_1fr] gap-2 text-sm">
                <Row label="Deposit transaction (tx)"><Address value={detail.timeline.depositTxHash} /></Row>
                <Row label="Closing transaction (tx)"><Address value={detail.timeline.closingTxHash} /></Row>
              </dl>
              {detail.timeline.stages.length === 0
                ? <p className="text-sm text-slate-500">No stage history.</p>
                : (
                  <table className="w-full text-sm">
                    <thead><tr><th className={TH}>Time</th><th className={TH}>Stage</th></tr></thead>
                    <tbody>
                      {detail.timeline.stages.map((st, n) => (
                        <tr key={n}>
                          <td className={TD}><Time ms={st.at} /></td>
                          <td className={TD}><Labeled labels={TRADE_STAGE_LABEL} code={st.stage} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
            </>
          )}
      </Card>

      <Card title="Terms consent">
        {!detail.termsConsents
          ? <p className="text-sm text-slate-500">No terms consent records.</p>
          : (
            <dl className="grid grid-cols-[9rem_1fr] gap-2 text-sm">
              <Row label="Seller">
                <Consent version={detail.termsConsents.sellerVersion} at={detail.termsConsents.sellerAgreedAt} />
              </Row>
              <Row label="Buyer">
                <Consent version={detail.termsConsents.buyerVersion} at={detail.termsConsents.buyerAgreedAt} />
              </Row>
            </dl>
          )}
      </Card>

      <Card title="OCR verdicts">
        {detail.verdicts.length === 0
          ? <p className="text-sm text-slate-500">No verdicts.</p>
          : (
            <table className="w-full text-sm">
              <thead><tr>
                <th className={TH}>Status</th><th className={TH}>Reason</th><th className={TH}>Requested round</th>
                <th className={TH}>Verdict round</th><th className={TH}>Received at</th>
              </tr></thead>
              <tbody>
                {detail.verdicts.map((v, n) => (
                  <tr key={n}>
                    <td className={TD}>
                      {v.status === null
                        ? <span className="text-slate-500">Checking (no verdict)</span>
                        : <span className={v.status === 'anomaly'
                            ? 'font-semibold text-red-700 dark:text-red-400'
                            : 'font-semibold text-emerald-700 dark:text-emerald-400'}>
                            <Labeled labels={VERDICT_LABEL} code={v.status} />
                          </span>}
                    </td>
                    <td className={TD}><Maybe value={v.reason} /></td>
                    <td className={TD}><Maybe value={v.requestedRound} /></td>
                    <td className={TD}><Maybe value={v.verdictRound} /></td>
                    <td className={TD}><Time ms={v.receivedAt} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </Card>

      <Card title="Proof readings">
        {/* 금액은 영수증에서 읽은 원화 원문 — USDE 로 바꾸지 않는다(명세 0-6). */}
        {detail.proofs.length === 0
          ? <p className="text-sm text-slate-500">No readings.</p>
          : (
            <table className="w-full text-sm">
              <thead><tr>
                <th className={TH}>Round</th><th className={TH}>Role</th><th className={TH}>Source</th>
                <th className={TH}>Direction</th><th className={TH}>Receipt time</th><th className={TH}>Amount</th>
                <th className={TH}>Received at</th><th className={TH}>Reading result</th><th className={TH}>During incident</th>
              </tr></thead>
              <tbody>
                {detail.proofs.map((p) => (
                  <tr key={p.proofId}>
                    <td className={TD}>{p.round}</td>
                    <td className={TD}>{PROOF_ROLE_LABEL[p.role] ?? p.role}</td>
                    <td className={TD}><Maybe value={p.source} /></td>
                    <td className={TD}><Maybe value={p.direction} /></td>
                    <td className={TD}><Maybe value={p.whenText} /></td>
                    <td className={TD}><Maybe value={p.amount === null ? null : String(p.amount)} /></td>
                    <td className={TD}><Time ms={p.receivedAt} /></td>
                    <td className={TD}>
                      <Labeled labels={PROOF_RESULT_LABEL} code={p.result ?? null} />
                      {p.resultReason && <> <Code>{p.resultReason}</Code></>}
                    </td>
                    <td className={TD}>{p.duringIncident ? 'Yes' : <Maybe value={null} />}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </Card>

      <Card title="Proof photos">
        {photos.length === 0
          ? <p className="text-sm text-slate-500">No stored proofs.</p>
          : (
            <div className="flex flex-wrap gap-4">
              {photos.map((p) => {
                const role = PROOF_ROLE_LABEL[p.role] ?? p.role
                return <ProofPhoto key={p.proofId} proofId={p.proofId} caption={`${role} · round ${p.round}`}
                  alt={`${role} round ${p.round} proof`} />
              })}
            </div>
          )}
      </Card>

      <ChatCard chat={detail.chat ?? null} />

      <Card title="Document files">
        {detail.documents.length === 0
          ? <p className="text-sm text-slate-500">No uploaded documents.</p>
          : (
            <table className="w-full text-sm">
              <thead><tr>
                <th className={TH}>No.</th><th className={TH}>File name</th><th className={TH}>Type</th>
                <th className={TH}>Size</th><th className={TH}>Uploaded by</th><th className={TH}>Uploaded at</th>
                <th className={TH}>Content</th>
              </tr></thead>
              <tbody>
                {detail.documents.map((d) => (
                  <tr key={d.id}>
                    <td className={TD}>#{d.id}</td>
                    <td className={`${TD} break-all`}>{d.fileName}</td>
                    <td className={TD}><Code>{d.contentType}</Code></td>
                    <td className={NUM}>{fileSize(d.size)}</td>
                    <td className={TD}><AdminRef adminId={d.adminId} loginId={d.adminLoginId} /></td>
                    <td className={TD}><Time ms={d.uploadedAt} /></td>
                    <td className={TD}>
                      <button type="button" className={LINK} onClick={() => setViewingDoc(d.id)}>View</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </Card>

      <Card title="Action history">
        {detail.events.length === 0
          ? <p className="text-sm text-slate-500">No action history.</p>
          : (
            <table className="w-full text-sm">
              <thead><tr>
                <th className={TH}>Time</th><th className={TH}>Kind</th><th className={TH}>Admin</th>
                <th className={TH}>Handed over to</th><th className={TH}>Action</th><th className={TH}>Text</th>
              </tr></thead>
              <tbody>
                {detail.events.map((e) => (
                  <tr key={e.id}>
                    <td className={TD}><Time ms={e.at} /></td>
                    <td className={TD}><Code>{e.kind}</Code></td>
                    <td className={TD}><AdminRef adminId={e.adminId} loginId={e.adminLoginId} /></td>
                    <td className={TD}>
                      {e.handoverTo ?? (e.toAdminId !== null ? `Admin #${e.toAdminId}` : <Maybe value={null} />)}
                    </td>
                    <td className={TD}>{e.action ? <Labeled labels={INCIDENT_EVENT_ACTION_LABEL} code={e.action} /> : <Maybe value={null} />}</td>
                    <td className={`${TD} whitespace-pre-wrap`}>
                      <Maybe value={e.text} />
                      <EventEvidence event={e} onView={setViewingDoc} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </Card>

      <MaterialMails incidentId={incidentId} detail={detail} onMail={onMail} />

      <IncidentActions incidentId={incidentId} detail={detail} onIncident={onIncident} onDocument={onDocument} />

      {viewingDoc !== null && (
        <DocumentDialog incidentId={incidentId} documentId={viewingDoc}
          doc={detail.documents.find((d) => d.id === viewingDoc) ?? null} onClose={() => setViewingDoc(null)} />
      )}
    </PhotoCacheCtx.Provider>
  )
}

/** 지갑 주소·체인 거래 번호 — 고정폭, 길어도 줄을 넘김. */
function Address({ value }: { value: string | null | undefined }) {
  return value ? <span className="break-all font-mono text-xs">{value}</span> : <Maybe value={null} />
}

/** 약관 동의 한 사람 — 「{판}판 · {동의 시각}」, 없으면 —. */
function Consent({ version, at }: { version: number | null; at: Millis | null }) {
  if (version === null) return <Maybe value={null} />
  return <>v{version} · <Time ms={at} /></>
}

/** 바이트 → 「1.2 MB」·「340 KB」·「512 B」(보이기만). */
function fileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`
  return `${bytes} B`
}

/**
 * 처리 기록 한 줄의 오프라인 증빙(처리 요청 줄 — 2026-10-01 새 칸): 확인한 기관·담당자·확인 날짜·문서번호·문서 파일.
 * 칸이 없으면(다른 줄·그 전 처리) 그리지 않는다.
 */
function EventEvidence({ event: e, onView }: { event: IncidentEvent; onView: (documentId: number) => void }) {
  const has = [e.institution, e.officer, e.confirmedOn, e.documentNo].some((v) => v) || (e.documentId ?? null) !== null
  if (!has) return null
  return (
    <dl className="mt-1 grid grid-cols-[6rem_1fr] gap-x-2 text-xs text-slate-600 dark:text-slate-300">
      <Row label="Institution confirmed"><Maybe value={e.institution ?? null} /></Row>
      <Row label="Officer"><Maybe value={e.officer ?? null} /></Row>
      <Row label="Confirmed on"><Maybe value={e.confirmedOn ?? null} /></Row>
      <Row label="Document no."><Maybe value={e.documentNo ?? null} /></Row>
      <Row label="Document file">
        {e.documentId === null || e.documentId === undefined
          ? <Maybe value={null} />
          : <button type="button" className={LINK} onClick={() => onView(e.documentId as number)}>View #{e.documentId}</button>}
      </Row>
    </dl>
  )
}

/**
 * 거래 대화(XM-58 `chat`) — 읽기만. 사장님 2026-10-01 「사고처리되면 즉시 제거한다.」: 사고처리 중인 거래의 대화만
 * 보이고, 거래가 끝나면(거래완료·거래취소) 서버가 지운다. 대화방이 열린 적이 없으면(증빙 단계 전) null.
 */
function ChatCard({ chat }: { chat: TradeChat | null }) {
  return (
    <Card title="Trade chat">
      {chat === null
        ? <p className="text-sm text-slate-500">The chat room was never opened.</p>
        : !chat.open
          ? <p className="text-sm text-slate-500">The trade ended, so the chat was deleted.</p>
          : chat.messages.length === 0
            ? <p className="text-sm text-slate-500">No messages.</p>
            : (
              <table className="w-full text-sm">
                <thead><tr>
                  <th className={TH}>Time</th><th className={TH}>Sender</th><th className={TH}>Text</th>
                </tr></thead>
                <tbody>
                  {chat.messages.map((m) => (
                    <tr key={m.id}>
                      <td className={TD}><Time ms={m.at} /></td>
                      <td className={TD}><Labeled labels={CHAT_SENDER_LABEL} code={m.sender} /></td>
                      <td className={`${TD} whitespace-pre-wrap`}>{m.text}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
    </Card>
  )
}

/**
 * 올린 문서 한 장 보기(XM-116) — 인증 헤더가 필요해 받아서 그린다. 사진은 그림으로, PDF 는 틀 안에. 볼 때마다 서버가
 * 사고 이력·감사 기록을 남긴다. 실패하면 실패했다고 말한다(자동 재시도 없음). 닫으면 object URL 을 해제한다.
 */
function DocumentDialog({ incidentId, documentId, doc, onClose }: {
  incidentId: number
  documentId: number
  doc: IncidentDocument | null
  onClose: () => void
}) {
  const [src, setSrc] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let url: string | null = null
    let gone = false
    fetchIncidentDocument(incidentId, documentId)
      .then((u) => { if (gone) URL.revokeObjectURL(u); else { url = u; setSrc(u) } })
      .catch(() => { if (!gone) setFailed(true) })
    return () => { gone = true; if (url) URL.revokeObjectURL(url) }
  }, [incidentId, documentId])

  const name = doc?.fileName ?? `Document #${documentId}`
  const pdf = doc?.contentType === 'application/pdf'
  return (
    <ViewDialog open title={`Document #${documentId}`} onClose={onClose}>
      <p className="mb-2 break-all text-xs text-slate-500">{name}</p>
      {failed
        ? <Notice tone="stop">Could not load the document.</Notice>
        : !src
          ? <p className="text-xs text-slate-500">Loading…</p>
          : pdf
            ? <iframe src={src} title={name} className="h-[70vh] w-full rounded border border-slate-200 dark:border-slate-700" />
            : <img src={src} alt={name} className="max-w-full rounded border border-slate-200 dark:border-slate-700" />}
    </ViewDialog>
  )
}

/**
 * 문이 열린 동안 증빙 사진(XM-59)을 사진마다 한 번만 받아 둔다 — 볼 때마다 서버가 사고 이력·감사 기록을 남기므로,
 * 「증빙 사진」 카드와 자료 메일 창이 같은 사진을 다시 받지 않게 한다. 실패도 그대로 둔다(자동 재시도 없음).
 * 문을 떠나면(화면이 사라지면) object URL 을 모두 해제한다.
 */
interface PhotoCache {
  get: (proofId: number) => Promise<string>
}

const PhotoCacheCtx = createContext<PhotoCache | null>(null)

function usePhotoCache(incidentId: number): PhotoCache {
  const urls = useRef(new Map<number, Promise<string>>())
  useEffect(() => {
    const held = urls.current
    return () => {
      for (const u of held.values()) u.then((url) => URL.revokeObjectURL(url)).catch(() => undefined)
      held.clear()
    }
  }, [])
  return useMemo(() => ({
    get(proofId: number) {
      let u = urls.current.get(proofId)
      if (!u) {
        u = fetchProofPhoto(incidentId, proofId)
        urls.current.set(proofId, u)
      }
      return u
    },
  }), [incidentId])
}

/**
 * 사진 한 장(XM-59) — 인증 헤더가 필요해 <img src> 로 직접 못 걸고, 받아서 그린다(문이 열린 동안 한 번 — PhotoCache).
 *
 * ⚠ **자동 재시도가 없다.** 실패하면 실패했다고 말한다.
 */
function ProofPhoto({ proofId, caption, alt }: { proofId: number; caption: ReactNode; alt: string }) {
  const cache = useContext(PhotoCacheCtx)
  const [src, setSrc] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (!cache) return
    let gone = false
    cache.get(proofId)
      .then((u) => { if (!gone) setSrc(u) })
      .catch(() => { if (!gone) setFailed(true) })
    return () => { gone = true }
  }, [cache, proofId])

  return (
    <figure className="w-64">
      <figcaption className="mb-1 text-xs text-slate-600 dark:text-slate-300">{caption}</figcaption>
      {failed
        ? <Notice tone="stop">Could not load the photo.</Notice>
        : src
          ? <img src={src} alt={alt} className="max-w-full rounded border border-slate-200 dark:border-slate-700" />
          : <p className="text-xs text-slate-500">Loading…</p>}
    </figure>
  )
}

/* ── 거래 자료 메일 — EA-26 카드 · EA-76 보내기 · EA-77 보기 (XM-114·XM-117) ─── */

/** 받는 분 고르기 — 사고 상세 줄(「판매자 (코인 보냄)」·「구매자 (코인 받음)」)과 같은 말. */
const PARTIES: { role: MaterialMailRole; label: string }[] = [
  { role: 'seller', label: 'Seller (sends coins)' },
  { role: 'buyer', label: 'Buyer (receives coins)' },
]

/** 자료 메일 제목·본문 칸 아래 안내 — 회원에게 영어로 간다. */
const MATERIAL_MAIL_HINT = 'This mail is sent to the member in English. Write the subject and body in English.'

/** 템플릿이 없을 때 — 서버 409 `mail_template_missing` 문구 그대로. */
const NO_TEMPLATE = 'There is no trade material mail template in effect. Create one first in System settings > Trade material mail.'

function roleLabel(role: string): string {
  return MATERIAL_MAIL_ROLE_LABEL[role] ?? role
}

/**
 * EA-26 의 카드 「거래 자료 메일」 — 보낸 기록(XM-58 `materialMails`, 오래된 것 먼저)과 [자료 메일 보내기].
 * 기록은 문을 연 사람 모두 본다(받는 주소가 들어 있어 서버가 문 뒤에서만 준다). 단추는 `incidents.handle`,
 * 사고 상태와 상관없이(종결 뒤도 — 계약 XM-114).
 */
function MaterialMails({ incidentId, detail, onMail }: {
  incidentId: number
  detail: Opened
  onMail: (m: IncidentMaterialMail) => void
}) {
  const { has } = useSession()
  const [sending, setSending] = useState(false)
  const [viewing, setViewing] = useState<IncidentMaterialMail | null>(null)
  const [done, setDone] = useState<string | null>(null)
  const mails = detail.materialMails

  return (
    <Card title={MATERIAL_MAIL}>
      <p className="mb-3 text-xs text-slate-500">
        Sends the trade materials to one party of the incident trade at that member's sign-up email. Each sending
        keeps a record of who sent what, when, to whom and with which template version.
      </p>
      {done && <div className="mb-3"><Notice tone="info">{done}</Notice></div>}
      {has('incidents.handle') && (
        <div className="mb-3">
          <Button tone="plain" onClick={() => { setDone(null); setSending(true) }}>Send material mail</Button>
        </div>
      )}
      {mails.length === 0
        ? <p className="text-sm text-slate-500">No material mail has been sent.</p>
        : (
          <table className="w-full text-sm">
            <thead><tr>
              <th className={TH}>Sent at</th><th className={TH}>Recipient</th><th className={TH}>Language</th>
              <th className={TH}>Member</th><th className={TH}>Address</th><th className={TH}>Template version</th>
              <th className={TH}>Attached photos</th><th className={TH}>Status</th><th className={TH}>Sent by</th>
              <th className={TH}>Content</th>
            </tr></thead>
            <tbody>
              {mails.map((m) => (
                <tr key={m.id}>
                  <td className={TD}><Time ms={m.createdAt} /></td>
                  <td className={TD}>{roleLabel(m.role)}</td>
                  <td className={TD}><Labeled labels={MAIL_LANGUAGE_LABEL} code={m.language} /></td>
                  <td className={TD}><MemberLink memberId={m.memberId} loginId={m.memberLoginId} /></td>
                  <td className={`${TD} break-all`}>{m.email}</td>
                  <td className={TD}>v{m.templateVersion}</td>
                  <td className={NUM}>{m.proofIds.length}</td>
                  <td className={TD}><MailStatus mail={m} /></td>
                  <td className={TD}><AdminRef adminId={m.adminId} loginId={m.adminLoginId} /></td>
                  <td className={TD}>
                    <button type="button" className={LINK} onClick={() => setViewing(m)}>View</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      {sending && (
        <SendMaterialMailDialog incidentId={incidentId} detail={detail}
          onSent={(m) => {
            onMail(m)
            setDone(`Sent the material mail to the ${roleLabel(m.role).toLowerCase()}.`)
          }}
          onClose={() => setSending(false)} />
      )}
      {viewing && <ViewMaterialMailDialog mail={viewing} onClose={() => setViewing(null)} />}
    </Card>
  )
}

/** 상태 — 보냄·실패(빨강, 오류 코드)·보내는 중. 표에 없는 값은 원문만(4-2). */
function MailStatus({ mail }: { mail: IncidentMaterialMail }) {
  return (
    <span className={mail.status === 'FAILED' ? 'text-red-700 dark:text-red-400' : undefined}>
      <Labeled labels={MATERIAL_MAIL_STATUS_LABEL} code={mail.status} />
      {mail.failure && <> <Code>{mail.failure}</Code></>}
    </span>
  )
}

/** XM-117 실패 문구 — 템플릿 없음은 서버와 같은 글, 그 밖은 4-4절(이메일 없음 등은 서버 글). */
function previewError(err: unknown): string {
  if (err instanceof ApiError && err.code === 'mail_template_missing') return NO_TEMPLATE
  return errorMessage(err, 'material_mail')
}

/**
 * EA-76 — 자료 메일 보내기 창. 사장님 2026-10-01 질문 23(「DB에서 가져오는 실제 거래내역 또는 기록에 대한 내용은
 * 관리자가 수정하지못하고 해당 내용은 자동으로 메일내용에 포함되어야하며 관리자가 볼때 에디터 내부가 아니라 그냥 일반
 * 출력만 하도록」):
 *
 * - 받는 사람을 고르면 XM-117 로 시행 중인 템플릿 제목·본문을 에디터에 채운다. 받는 사람을 바꾸면 다시 채운다(고친
 *   글은 그때 사라진다). 2026-10-02 부터 영어로만 — 언어 고르기는 없다(사장님 「일단 영어로만」, 거래소백엔드 1ab5d45 —
 *   앞의 「언어(한국어/English)」 고르기(질문 23)는 이 말씀으로 없어짐).
 * - 에디터에는 제목·본문만 있고 관리자가 고친다. 그 아래 거래 자료(DB 값)와 붙일 사진은 글로만 보인다 — 고칠 수 없다.
 * - 「메일 보내기」 → XM-114 {role, subject, body}. 거래 자료는 서버가 DB 에서 다시 만들어 붙인다. 한글이 있으면 잠근다.
 *
 * ⚠ **PIN 에 막혀도 기억하지 않는다** — 이 화면의 다른 쓰기와 같게(레거시 그대로). 개인 자료가 든 메일을
 * 나중에 혼자 보내지 않는다.
 */
function SendMaterialMailDialog({ incidentId, detail, onSent, onClose }: {
  incidentId: number
  detail: Opened
  onSent: (m: IncidentMaterialMail) => void
  onClose: () => void
}) {
  const w = useWrite()
  const [role, setRole] = useState<MaterialMailRole | null>(null)
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const preview = useQuery({
    queryKey: ['material-mail-preview', incidentId, role],
    queryFn: () => previewIncidentMaterialMail(incidentId, role as MaterialMailRole),
    enabled: role !== null,
    staleTime: 0,
    retry: false,
  })
  const i = detail.incident
  const t = detail.trade
  const memberOf = (r: MaterialMailRole) =>
    r === 'seller' ? (t?.sellerMemberId ?? i.sellerMemberId) : (t?.buyerMemberId ?? i.buyerMemberId)

  // 받는 사람이 바뀌어 새 답이 오면 그 글로 에디터를 다시 채운다 — 렌더 중에 고치는 React 방식.
  const key = role
  const [filledFor, setFilledFor] = useState<string | null>(null)
  const data = preview.data
  if (key !== null && data && data.role === role && filledFor !== key) {
    setFilledFor(key)
    setSubject(data.subject)
    setBody(data.body)
  }
  const filled = key !== null && filledFor === key && preview.isSuccess
  // 회원에게 영어로 가는 메일 — 한글이 있으면 [Send mail] 잠금(사장님 2026-10-02 「일단 영어로만」, 서버도 거절).
  const ready = filled && subject.trim() !== '' && body.trim() !== '' && !hasKorean(subject) && !hasKorean(body)

  async function submit() {
    if (role === null || !ready) return
    const send = async () => { onSent(await sendIncidentMaterialMail(incidentId, role, { subject, body })) }
    await submitWrite(w, 'Send material mail', send, onClose, {
      remember: false,
      message: (err) => errorMessage(err, 'material_mail'),
    })
  }

  return (
    <Confirm open wide title="Send material mail" action="Send mail" irreversible
      busy={w.busy} error={w.error} onCancel={onClose} onConfirm={() => { void submit() }}
      disabled={!ready}>
      <div className="flex flex-col gap-3">
        <p>Sends the materials of this incident trade to the chosen party's sign-up email.</p>
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 font-medium">Recipient</legend>
          {PARTIES.map((p) => {
            const memberId = memberOf(p.role)
            return (
              <label key={p.role} className="flex items-center gap-2">
                <input type="radio" name="material-mail-role" checked={role === p.role}
                  onChange={() => setRole(p.role)} />
                <span>{p.label} · {memberId === null ? <Maybe value={null} /> : `Member #${memberId}`}</span>
              </label>
            )
          })}
        </fieldset>

        {key === null
          ? <p className="text-xs text-slate-500">Choose the recipient to fill in the mail.</p>
          : preview.isError
            ? <Notice tone="stop">{previewError(preview.error)}</Notice>
            : !filled
              ? <p className="text-xs text-slate-500">Loading…</p>
              : (
                <section aria-label="Mail to send" className="flex flex-col gap-3">
                  <dl className="grid grid-cols-[6rem_1fr] gap-1.5">
                    <Row label="Recipient">{roleLabel(role as string)}</Row>
                    <Row label="Recipient address">
                      This member's sign-up email — the server finds it in the member information, and the
                      address is kept in the record after sending.
                    </Row>
                    <Row label="Template">v{data?.templateVersion}</Row>
                  </dl>
                  <Field label="Subject (required · 200 chars)" value={subject} maxLength={200}
                    onChange={(e) => setSubject(e.target.value)} />
                  <TextArea label="Body (required · 200,000 chars)" value={body} maxLength={200000} rows={8}
                    onChange={(e) => setBody(e.target.value)} />
                  <EnglishOnlyNote texts={[subject, body]} hint={MATERIAL_MAIL_HINT}
                    blocked="The subject or body has Korean letters. [Send mail] stays off until they are written in English only." />
                  <div>
                    <h4 className="mb-1 text-xs font-semibold text-slate-500">Trade materials added after the body (cannot be edited)</h4>
                    <div className="rounded border border-slate-200 bg-slate-50 p-3 text-xs dark:border-slate-700 dark:bg-slate-800"
                      aria-label="Trade materials" role="region">
                      <Plain text={data?.materials ?? ''} />
                    </div>
                  </div>
                  <div>
                    <h4 className="mb-1 text-xs font-semibold text-slate-500">Attached photos (this member's own)</h4>
                    {(data?.photos ?? []).length === 0
                      ? <p className="text-xs text-slate-500">This member has not uploaded any photos.</p>
                      : (
                        <div className="flex flex-wrap gap-3">
                          {(data?.photos ?? []).map((ph) => (
                            <MailPhoto key={ph.proofId} photo={ph} />
                          ))}
                        </div>
                      )}
                  </div>
                  <Notice tone="warn">
                    The photos, readings and personal information of the other party
                    ({roleLabel(role === 'seller' ? 'buyer' : 'seller')}), and the verdict comparing the two receipts, are
                    not included.
                  </Notice>
                </section>
              )}
      </div>
    </Confirm>
  )
}

/** 붙는 사진 한 장 — 사진 번호·올린 시각·판독 결과(고른 언어 글), 파일이 있으면 그림(XM-59, 문이 열린 동안 한 번). */
function MailPhoto({ photo: ph }: { photo: MaterialMailPhoto }) {
  const caption = (
    <>
      Photo {ph.round} · <Time ms={ph.uploadedAt} /> · {ph.resultLabel}
      {!ph.attached && <> · no file</>}
    </>
  )
  return ph.attached
    ? <ProofPhoto proofId={ph.proofId} caption={caption} alt={`Photo ${ph.round}`} />
    : <figure className="w-64"><figcaption className="text-xs text-slate-600 dark:text-slate-300">{caption}</figcaption></figure>
}

/**
 * EA-77 — 보낸 자료 메일 한 건: 실제로 나간 것 그대로(글로만) — 언어·제목·본문·거래 자료(2026-10-01 14번부터 따로
 * 남음 — 나간 메일 본문 = 본문 + 빈 줄 + 거래 자료). 그 전 기록은 본문에 거래 자료가 들어 있다.
 */
function ViewMaterialMailDialog({ mail, onClose }: { mail: IncidentMaterialMail; onClose: () => void }) {
  return (
    <ViewDialog open title={`Material mail #${mail.id}`} onClose={onClose}>
      <dl className="mb-3 grid grid-cols-[7rem_1fr] gap-1.5">
        <Row label="Status"><MailStatus mail={mail} /></Row>
        <Row label="Recipient">{roleLabel(mail.role)}</Row>
        <Row label="Language"><Labeled labels={MAIL_LANGUAGE_LABEL} code={mail.language} /></Row>
        <Row label="Member">{mail.memberLoginId ?? `Member #${mail.memberId}`}</Row>
        <Row label="Address"><span className="break-all">{mail.email}</span></Row>
        <Row label="Template version">v{mail.templateVersion}</Row>
        <Row label="Attached proofs">
          {mail.proofIds.length === 0
            ? <Maybe value={null} />
            : <span className="font-mono">{mail.proofIds.map((id) => `#${id}`).join(', ')}</span>}
        </Row>
        <Row label="Sent by"><AdminRef adminId={mail.adminId} loginId={mail.adminLoginId} /></Row>
        <Row label="Sending started"><Time ms={mail.createdAt} /></Row>
        <Row label="Finished"><Time ms={mail.finishedAt} /></Row>
      </dl>
      <div className="rounded border border-slate-200 p-3 dark:border-slate-700">
        <dl className="grid grid-cols-[6rem_1fr] gap-1.5">
          <Row label="Subject"><Plain text={mail.subject} /></Row>
          <Row label="Body"><Plain text={mail.body} /></Row>
          {mail.materials !== null && <Row label="Trade materials"><Plain text={mail.materials} /></Row>}
        </dl>
      </div>
    </ViewDialog>
  )
}

/* ── EA-27 처리 · EA-28 외부 인계 ────────────────────────────────── */

/**
 * 처리 창의 두 가지(거래 흐름 04 9단계 — 사장님 원문 9 「관리자페이지에서 거래완료 또는 거래취소상태로
 * 스마트컨트렉트 상태를 변경할 수 있다」). 무엇이 일어나는지는 거래소 01 6-10·계약 XM-61 그대로.
 */
const ACTIONS: { action: IncidentAction; what: string }[] = [
  { action: 'CONFIRM', what: 'Escrow Confirm — the buyer receives the deposit − the fee.' },
  { action: 'CANCEL', what: 'Escrow Cancel — the seller gets the deposit back less the gas (0.02 USDE under 100 USDE, 0.1% from 100 USDE).' },
]

/** 문서 파일 크기 한도 — 서버와 같은 8MB(계약 XM-115, 거래소백엔드 multipart max-file-size). */
const DOCUMENT_MAX_BYTES = 8 * 1024 * 1024

/**
 * 처리(EA-27)·외부 인계(EA-28).
 *
 * 처리 창 칸(2026-10-01 사장님 질문 30 「1」 — 처리 근거 외에 확인한 기관·담당자·확인 날짜(yyyy-mm-dd)·문서번호·문서
 * 파일(공문 사진·PDF), 「2」 — 모든 칸 필수, 하나라도 비면 [처리 요청]이 눌리지 않고 서버도 거절): 칸 순서는 말씀
 * 순서 그대로. 문서 파일은 [처리 요청]을 누를 때 XM-115 로 먼저 올리고 그 번호로 XM-61 을 보낸다. 올린 문서는
 * 지우지 않고 사고에 남는다(계약) — 처리가 실패해도 같은 파일이면 다시 올리지 않는다.
 */
function IncidentActions({ incidentId, detail, onIncident, onDocument }: {
  incidentId: number
  detail: Opened
  onIncident: (i: IncidentCase) => void
  onDocument: (d: IncidentDocument) => void
}) {
  const { has } = useSession()
  const qc = useQueryClient()
  const [mode, setMode] = useState<'act' | 'handover' | null>(null)
  const [action, setAction] = useState<IncidentAction | null>(null)
  const [institution, setInstitution] = useState('')
  const [officer, setOfficer] = useState('')
  const [confirmedOn, setConfirmedOn] = useState('')
  const [documentNo, setDocumentNo] = useState('')
  const [file, setFile] = useState<File | null>(null)
  // 이 창에서 이미 올린 문서(같은 파일이면 다시 올리지 않는다).
  const [uploaded, setUploaded] = useState<{ file: File; doc: IncidentDocument } | null>(null)
  const [basis, setBasis] = useState('')
  const [handoverTo, setHandoverTo] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)

  // 버튼 키 `incidents.handle`(명세 0-5, 계약 7절).
  if (!has('incidents.handle')) return null
  const status = detail.incident.status
  const canAct = status === 'OPEN'
  const canHandOver = status !== 'CLOSED'
  if (!canAct && !canHandOver && !done) return null

  function close() {
    setMode(null); setAction(null); setBasis(''); setHandoverTo(''); setNote(''); setError(null)
    setInstitution(''); setOfficer(''); setConfirmedOn(''); setDocumentNo(''); setFile(null); setUploaded(null)
  }

  const tooLarge = file !== null && file.size > DOCUMENT_MAX_BYTES
  const actReady = action !== null
    && institution.trim() !== '' && officer.trim() !== '' && localDayRange(confirmedOn) !== null
    && documentNo.trim() !== '' && file !== null && !tooLarge && basis.trim() !== ''

  /** 문서 파일 — 이 창에서 올린 그 파일이면 그 문서, 아니면 지금 올린다(XM-115). */
  async function documentFor(f: File): Promise<IncidentDocument> {
    if (uploaded && uploaded.file === f) return uploaded.doc
    const doc = await uploadIncidentDocument(incidentId, f)
    setUploaded({ file: f, doc })
    onDocument(doc)
    return doc
  }

  const run = () => {
    if (busy || !mode) return
    if (mode === 'act' && !actReady) return
    if (mode === 'handover' && handoverTo.trim() === '') return
    setBusy(true); setError(null)
    // ⚠ PIN 에 막혀도 기억하지 않는다(레거시 그대로) — 돈이 움직이는 요청을 나중에 혼자
    // 보내지 않는다. 키패드를 지난 뒤 사람이 다시 누른다.
    const call = mode === 'act'
      ? documentFor(file as File)
          .then((doc) => actOnIncident(incidentId, action as IncidentAction, basis.trim(), {
            institution: institution.trim(), officer: officer.trim(), confirmedOn,
            documentNo: documentNo.trim(), documentId: doc.id,
          }))
          .then((inc) => {
            onIncident(inc)
            return 'The action was requested. The incident closes once it is confirmed on the chain.'
          })
      : handOverIncident(incidentId, handoverTo.trim(), note.trim() || undefined).then((inc) => {
          onIncident(inc)
          return 'The handover was recorded. The incident stays open.'
        })
    call
      .then((msg) => {
        setDone(msg)
        close()
        void qc.invalidateQueries({ queryKey: ['incidents'] })
      })
      .catch((e: unknown) => {
        if (mode === 'handover' && e instanceof ApiError && e.code === 'conflict') {
          setError('This incident is already closed.')
        } else {
          setError(errorMessage(e))
        }
      })
      .finally(() => setBusy(false))
  }

  const t = detail.trade
  const blocked = mode === 'act' ? !actReady : handoverTo.trim() === ''

  return (
    <Card title="Actions">
      {done && <div className="mb-3"><Notice tone="info">{done}</Notice></div>}
      <div className="flex flex-wrap gap-2">
        {canAct && <Button tone="plain" onClick={() => setMode('act')}>Process</Button>}
        {canHandOver && <Button tone="plain" onClick={() => setMode('handover')}>External handover</Button>}
      </div>

      <Confirm
        open={mode !== null}
        title={mode === 'handover' ? 'External handover' : 'Process incident'}
        action={mode === 'handover' ? 'Hand over' : 'Request action'}
        busy={busy} error={error} danger={mode === 'act'} wide={mode === 'act'}
        onCancel={close}
        onConfirm={run}
        disabled={blocked}>
        {mode === 'handover' ? (
          <div className="flex flex-col gap-3">
            <p>Records a handover to an outside party. The incident is not closed. The admin sends the records directly.</p>
            <Field label="Handed over to (required · 200 chars)" value={handoverTo} maxLength={200}
              onChange={(e) => setHandoverTo(e.target.value)} />
            <TextArea label="Note (2,000 chars)" value={note} maxLength={2000}
              onChange={(e) => setNote(e.target.value)} />
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {/* 사장님 2026-10-01 질문 28 「1」(원문 9 「오프라인증빙을 통해서 금융당국이나 정부기관에 확인 후」) */}
            <p>
              Sends the action to the escrow. <strong>Money moves.</strong> Do this only after confirming the
              offline evidence with the financial authorities or a government agency, and write the grounds.
            </p>
            {t && (
              <dl className="grid grid-cols-[6rem_1fr] gap-1.5">
                <Row label="Trade quantity"><span className="font-mono">{usde(t.amount)}</span></Row>
                <Row label="Fee"><span className="font-mono"><Maybe value={usde(t.fee)} /></span></Row>
                <Row label="Deposit amount"><span className="font-mono"><Maybe value={usde(t.depositAmount)} /></span></Row>
              </dl>
            )}
            <fieldset className="flex flex-col gap-2">
              {ACTIONS.map(({ action: a, what }) => (
                <label key={a} className="flex items-start gap-2">
                  <input type="radio" name="incident-action" className="mt-1" checked={action === a}
                    onChange={() => setAction(a)} />
                  <span>
                    {INCIDENT_ACTION_LABEL[a]} <Code>{a}</Code>
                    <span className="block text-xs text-slate-500">{what}</span>
                  </span>
                </label>
              ))}
            </fieldset>
            <Field label="Institution confirmed (required · 200 chars)" value={institution} maxLength={200}
              onChange={(e) => setInstitution(e.target.value)} />
            <Field label="Officer (required · 100 chars)" value={officer} maxLength={100}
              onChange={(e) => setOfficer(e.target.value)} />
            <DateField label="Confirmed on (required · yyyy-mm-dd)" name="Confirmed on" value={confirmedOn} onChange={setConfirmedOn} />
            <Field label="Document no. (required · 100 chars)" value={documentNo} maxLength={100}
              onChange={(e) => setDocumentNo(e.target.value)} />
            <DocumentFileField file={file} uploaded={uploaded !== null && uploaded.file === file ? uploaded.doc : null}
              tooLarge={tooLarge} onChange={setFile} />
            <TextArea label="Grounds (required · 4,000 chars)" value={basis} maxLength={4000}
              onChange={(e) => setBasis(e.target.value)} />
          </div>
        )}
      </Confirm>
    </Card>
  )
}

/**
 * 문서 파일 칸 — 확인한 공문의 사진·PDF(JPEG·PNG·PDF, 8MB 이하 — 계약 XM-115). 고른 파일은 [처리 요청] 때 올라간다.
 * 8MB 를 넘으면 그 자리에서 말하고 [처리 요청]을 잠근다(서버도 413).
 */
function DocumentFileField({ file, uploaded, tooLarge, onChange }: {
  file: File | null
  uploaded: IncidentDocument | null
  tooLarge: boolean
  onChange: (f: File | null) => void
}) {
  const id = useId()
  const picker = useRef<HTMLInputElement>(null)
  return (
    <div className="flex flex-col gap-1 text-sm">
      <label htmlFor={id} className="font-medium">Document file (required · photo or PDF · 8MB)</label>
      {/*
        브라우저 파일 칸은 단추·「고른 파일 없음」 글을 브라우저(컴퓨터) 언어로 그린다 — 칸은 숨기고 우리 영어 단추와
        고른 파일 이름을 보인다(사장님 2026-10-02 「일단 영어로만」). 칸은 그대로 있어 이름표·파일 넣기는 같다.
      */}
      <input id={id} ref={picker} type="file" accept="image/jpeg,image/png,application/pdf"
        className="sr-only" tabIndex={-1} onChange={(e) => onChange(e.target.files?.[0] ?? null)} />
      <div className="flex flex-wrap items-center gap-2">
        <Button tone="plain" onClick={() => picker.current?.click()}>Choose file</Button>
        <span className="text-xs text-slate-500">
          {file
            ? <>{file.name} · {fileSize(file.size)}{uploaded && <> · uploaded (document #{uploaded.id})</>}</>
            : 'No file chosen'}
        </span>
      </div>
      {tooLarge && <Notice tone="stop">Only files of 8MB or less can be uploaded.</Notice>}
      <p className="text-xs text-slate-500">
        The file is uploaded to this incident first when you press [Request action]. Uploaded documents are not
        deleted and stay with the incident.
      </p>
    </div>
  )
}
